import React, { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, useIntl } from 'react-intl';
import { useApolloClient, useQuery } from '@apollo/client';
import { ReactComponent as ChartLineIcon } from '../../shared/icons/chart-line-icon.svg';
import { usePagination, useSortBy, useTable } from 'react-table';
import { Button } from 'react-bootstrap';
import PropdocsModal from '../../components/modal/PropdocsModal';
import dayjs from 'dayjs';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { getDocumentTemplates, getOrganizationsTemplatesByUser, getTemplateFolders } from '../../api/queries_v2';
import { fillPlaceholders, paths, useNavigate } from '../../routing';
import slugify from '../../utils/slugify';
import { useTeamInfo } from '../../team-id-context';
import { usePermissions } from '../../auth';
import { CreatedTemplateTypes, useUserRestrictions } from '../../auth/user-restrictions';
import { Dropdown, DropdownBody, DropdownItem, DropdownToggle, DropdownToggleOrange } from '../../components/dropdown';
import SearchInput from '../../components/search-input';
import { ContentHeader } from '../../components/private-layout';
import { Table } from '../../components/table';
import { Checkbox } from '../../components/checkbox-or-radio-field';
import LoadingSpinner from '../../components/loading-spinner';
import Controls from './Controls';
import { TemplateStatus } from './templateConstants';
import AddNewTemplateFormik from '../../forms/AddNewTemplateFormContainer';
import { useDocumentTeamPermissions } from '../document-wizard/shared/hooks/use-document-team-permissions/use-document-team-permissions';
import { Permissions } from '../../shared/constants/permissions';
import { TemplateType } from '../../consts/forms-types';
import styles from './styles.module.scss';
import { ModalActionsEnum } from '../../components/document-table/DocumentTableConstants';
import { FileUploadModal } from '../../components/shared/modals/fileUploadModal';
import { useModals } from '../../contexts/ModalsContext';
import SubscriptionRequiredModal from '../modals/subscriptionModal/SubscriptionRequiredModal';
import Tooltip from '../../components/tooltip';
import { ReactComponent as FilterIcon } from '../../shared/icons/filter-icon.svg';
import { ReactComponent as PlusIcon } from '../../shared/icons/plus-icon.svg';
import { ReactComponent as InfoIcon } from '../../shared/icons/info-unfilled-icon.svg';
import { ReactComponent as HomeIcon } from '../../shared/icons/home-icon.svg';
import { ReactComponent as FolderIcon } from '../../shared/icons/folder-icon.svg';
import { ReactComponent as GlobeIcon } from '../../shared/icons/globe-icon.svg';
import { fuzzyInputSearch } from '../deals/helpers/determineDealsActionModalMessage';
import { TitleFormattedMessage } from '../../components/titleFormattedMessage';
import { DocumentTemplateType, getTemplateType } from '../../shared/templateType';
import { ContentBody } from '../../components/private-layout/ContentBody';
import { MobileTable } from '../../components/mobile-layout/MobileTable';
import { MobileTemplateItem } from '../../components/mobile-layout/MobileTemplateItem';
import { MobilePagination } from '../../components/mobile-layout/MobilePagination';
import FilterSidebar from './components/filterSidebar';
import { statusOptions, templateStatusMessages, templateTypeMessages, templateTypeOptions } from './constants';
import { CreateFolderModal } from './components/CreateFolderModal';
import FolderControls from './FolderControls';
import { DropdownProvider } from '../../contexts/DropdownContext';
import TemplateAnalyticsSlideout from './components/TemplateAnalytics/TemplateAnalyticsSlideout';
import { TemplatePublishStatusBadge } from '../../components/templates/TemplatePublishStatusBadge';
import {
  getListTemplatePublishStatus,
  TemplatePublishStatus,
} from '../../components/templates/templatePublishStatus';

enum AddTemplateActionStatus {
  IMPORT_FROM_WORD = 'Import From Word',
  UPLOADED_PDF = 'Upload PDF',
  BLANK_TEMPLATE = 'Blank Template',
}

type CreateTemplateAction = {
  text: AddTemplateActionStatus;
  allowedType: CreatedTemplateTypes;
  open: () => void;
  isAvailable: boolean;
};

type ColumnsCellType = {
  cell: {
    value: string;
    row: {
      values: {
        template: {
          id: string;
          organizationName: string;
          fromOrganization?: boolean;
          description?: string;
          isFolder?: boolean;
          isVirtualSharedFolder?: boolean;
          sharedFromTeam?: { id: string; name: string };
        };
        name: string;
        status?: string;
        templateType?: TemplateType;
      };
    };
  };
};

const defaultTypeOptions = new Set<string>([
  DocumentTemplateType.PDF,
  DocumentTemplateType.FULLY_EDITABLE,
  DocumentTemplateType.RESTRICTED,
]);
// Drafts are listed by default so the people writing them can find them.
const defaultTemplateStatusOptions = new Set<string>([TemplateStatus.ACTIVE, TemplateStatus.DRAFT]);

// Workspace-shared and organization templates belong to another team; their
// draft state isn't this team's to act on.
const ownTemplatePublishStatus = (template: any): TemplatePublishStatus | undefined =>
  template.fromOrganization || template.sharedFromTeam ? undefined : getListTemplatePublishStatus(template);

const templateFilterStatus = (template: any): string =>
  ownTemplatePublishStatus(template) === 'draft'
    ? TemplateStatus.DRAFT
    : template.hidden
      ? TemplateStatus.INACTIVE
      : TemplateStatus.ACTIVE;

// Custom sort functions that prioritize folders first
const createFolderPrioritySortFn = (sortFn: (a: any, b: any) => number) => {
  return (a: any, b: any) => {
    // Always show folders first, regardless of sorting
    if (a.original.type === 'folder' && b.original.type === 'template') return -1;
    if (a.original.type === 'template' && b.original.type === 'folder') return 1;

    // If both are the same type, apply the provided sorting function
    return sortFn(a, b);
  };
};

const nameSortFn = (a: any, b: any) => {
  const nameA = a.values.name || '';
  const nameB = b.values.name || '';
  return nameA.localeCompare(nameB);
};
const statusSortFn = (a: any, b: any) => {
  const statusA = a.values.status || '';
  const statusB = b.values.status || '';
  return statusA.localeCompare(statusB);
};
const dateSortFn = (a: any, b: any) => {
  if (!a.values.updatedAt || !b.values.updatedAt) return 0;
  return new Date(b.values.updatedAt).getTime() - new Date(a.values.updatedAt).getTime();
};

const TemplatesLandingPage = () => {
  const { activeModal, setActiveModal } = useModals();
  const { formatMessage } = useIntl();
  const { teamId, teamName } = useTeamInfo();
  const navigate = useNavigate();
  const { restrictionsLoading, hasAccessToTemplates, creatableTemplateTypes } = useUserRestrictions();
  const { isAdmin, hasPermission } = usePermissions() as {
    isAdmin: boolean;
    hasPermission: (permission: string) => boolean;
  };
  const apolloClient = useApolloClient();
  const [showAddTemplate, setShowAddTemplate] = useState(false);
  const [showEditTemplate, setShowEditTemplate] = useState(false);
  const [selectedTemplateType, setSelectedTemplateType] = useState<TemplateType | null>(null);
  const [filterText, setFilterText] = useState('');
  const [selectedCreator, setSelectedCreator] = useState<string>('');
  const [filterStatuses, setFilterStatuses] = React.useState(new Set(defaultTemplateStatusOptions));
  const [filterTemplateTypes, setFilterTemplateTypes] = React.useState(new Set(defaultTypeOptions));
  
  // Simplified filter label - always just "Filters"
  const getCombinedFilterLabel = "Filters";
  const [showFilter, setShowFilter] = useState(false);
  const [showCreatorDropdown, setShowCreatorDropdown] = useState(false);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [sorting, setSorting] = useState('');
  const [isSubscriptionModalActive, setSubscriptionModalActive] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [currentVirtualFolderId, setCurrentVirtualFolderId] = useState<string | null>(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [folderPath, setFolderPath] = useState<Array<{ id: string | null; name: string; isVirtual?: boolean }>>([
    { id: null, name: 'Templates' },
  ]);
  const [selectedFolderForCreation, setSelectedFolderForCreation] = useState<string | null>(null);
  const canEdit = hasPermission(Permissions.Edit);
  const canManageTemplates = hasPermission(Permissions.ManageTemplates);
  const canEditTemplates = hasPermission(Permissions.EditTemplates);
  const isTemplateEditable = isAdmin || canEdit || canManageTemplates;
  // Template editors create drafts (blank or Word import). PDF upload, folders
  // and the restricted / Word-export settings stay with template managers: an
  // uploaded PDF is live the moment it exists, with no draft to review.
  const canPublishTemplates = !!(isAdmin || canManageTemplates);
  const canCreateTemplates = canPublishTemplates || !!canEditTemplates;

  // All hooks must be called before any conditional returns
  const {
    loading: isDocumentTemplatesDataLoading,
    data: documentTemplatesData,
    refetch,
  } = useQuery(getDocumentTemplates as any, {
    fetchPolicy: 'network-only',
    skip: !teamId || !hasAccessToTemplates,
    variables: {
      teamId,
      templateType: 'agreement',
      includeContent: false,
    },
  });

  const { loading: isUserOrgsTemplatesDataLoading, data: userOrgsTemplatesData } = useQuery(
    getOrganizationsTemplatesByUser,
    {
      fetchPolicy: 'network-only',
      skip: !teamId || !hasAccessToTemplates,
      variables: {
        teamId,
        templateType: 'agreement',
        includeContent: false,
      },
    }
  );

  const {
    loading: isTemplateFoldersLoading,
    data: templateFoldersData,
    refetch: refetchFolders,
  } = useQuery(getTemplateFolders as any, {
    fetchPolicy: 'network-only',
    skip: !teamId,
    variables: {
      teamId,
      folderType: 'template',
    },
  });

  const allTemplates = useMemo(() => {
    if (isDocumentTemplatesDataLoading || isUserOrgsTemplatesDataLoading || !documentTemplatesData) {
      return [];
    }

    const userTemplatesIds = documentTemplatesData?.getDocumentTemplates.map((template: any) => template.id);
    const organizationTemplates =
      userOrgsTemplatesData?.organizationsTemplatesByUser
        .filter((orgTemplate: any) => !userTemplatesIds?.includes(orgTemplate.id))
        .map((orgTemplate: any) => {
          return { ...orgTemplate, fromOrganization: true };
        }) ?? [];
    const mergedTemplates = [...(documentTemplatesData?.getDocumentTemplates ?? []), ...organizationTemplates];

    return mergedTemplates;
  }, [isDocumentTemplatesDataLoading, documentTemplatesData, isUserOrgsTemplatesDataLoading, userOrgsTemplatesData]);

  const allCreators = useMemo(() => {
    if (!allTemplates) {
      return [];
    }

    const creators = new Set<string>();

    // Always add "Your Team" as an option first, even if no templates yet
    if (teamName) {
      creators.add(`${teamName} (Your Team)`);
    }

    allTemplates.forEach((template: any) => {
      // Add regular team names
      if (template?.team?.name) {
        if (template?.team?.id === teamId) {
          creators.add(`${template.team.name} (Your Team)`);
        } else {
          creators.add(template.team.name);
        }
      }
      // Add shared template source teams
      if (template?.sharedFromTeam?.name) {
        creators.add(template.sharedFromTeam.name);
      }
    });

    return Array.from(creators).sort((a: any, b: any) => {
      // Keep "Your Team" at the top
      if (a.includes('(Your Team)')) return -1;
      if (b.includes('(Your Team)')) return 1;
      return a.localeCompare(b);
    });
  }, [allTemplates, teamId, teamName]);

  // Context-aware permission calculations - must be after allCreators is defined
  const { selectedCreatorIsOwnTeam, canCreateTemplatesGlobally, canEditInSelectedContext } = useMemo(() => {
    const isOwnTeam =
      selectedCreator?.includes('(Your Team)') ||
      (!selectedCreator && allCreators.length > 0 && allCreators.find((c) => c.includes('(Your Team)')));

    const canCreateGlobally = canCreateTemplates && creatableTemplateTypes && creatableTemplateTypes.length > 0;

    const canEditInContext = isOwnTeam && isTemplateEditable;

    return {
      selectedCreatorIsOwnTeam: isOwnTeam,
      canCreateTemplatesGlobally: canCreateGlobally,
      canEditInSelectedContext: canEditInContext,
    };
  }, [selectedCreator, allCreators, isTemplateEditable, canCreateTemplates, creatableTemplateTypes]);

  useEffect(() => {
    // Set default to "Your Team" if available, otherwise first creator
    const yourTeamCreator = allCreators.find((creator) => creator.includes('(Your Team)'));
    const defaultCreator = yourTeamCreator || allCreators[0] || '';
    if (!selectedCreator && defaultCreator) {
      setSelectedCreator(defaultCreator);
    }
  }, [allCreators, selectedCreator]);

  // Reset virtual folder navigation when creator changes
  useEffect(() => {
    setCurrentVirtualFolderId(null);
    setFolderPath([{ id: null, name: 'Templates' }]);
  }, [selectedCreator]);

  const templateIsUpdated = useCallback(() => {
    setShowAddTemplate(false);
    refetch();
  }, [refetch]);

  const onAddTemplateModalHide = () => {
    setShowAddTemplate(false);
    setSelectedTemplateType(null);
  };

  const onAddTemplateShow = (templateType: TemplateType) => {
    setShowAddTemplate(true);
    setSelectedTemplateType(templateType);
  };

  const handleDocFileUploadModal = useCallback(() => {
    return setActiveModal(ModalActionsEnum.UPLOAD_DOC_FILE);
  }, [setActiveModal]);

  const handleFileUploadModal = useCallback(() => {
    return setActiveModal(ModalActionsEnum.UPLOAD_FILE);
  }, [setActiveModal]);

  const permissions = useDocumentTeamPermissions({ apolloClient, teamId });

  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: <FormattedMessage id="title.deals.template-title" defaultMessage="Template Name" />,
        accessor: 'name',
        sortType: createFolderPrioritySortFn(nameSortFn),
        Cell: ({
          cell: {
            row: {
              values: { name, template },
              original,
            },
          },
        }: ColumnsCellType & { cell: { row: { original: any } } }) => {
          return (
            <div className={styles.headerTitleTableColumn}>
              {template.isFolder &&
                (template.isVirtualSharedFolder ? (
                  <GlobeIcon
                    className={styles.folderIcon}
                    style={{ marginRight: '8px', width: '16px', height: '16px', fill: '#006c90' }}
                  />
                ) : (
                  <FolderIcon
                    className={styles.folderIcon}
                    style={{ marginRight: '8px', width: '16px', height: '16px', fill: '#006c90' }}
                  />
                ))}
              {template.description && !!template.description.length && (
                <Tooltip
                  testId={`template-landing-page-template-${template.id}-tooltip`}
                  placement="right"
                  icon={<InfoIcon className="text-primary mr-1" />}
                >
                  {template.description}
                </Tooltip>
              )}
              {filterText && original.folderPath ? (
                <div>
                  <FormattedMessage id="cta.go-to.details" defaultMessage={name} />
                  <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>{original.folderPath}</div>
                </div>
              ) : (
                <FormattedMessage id="cta.go-to.details" defaultMessage={name} />
              )}
            </div>
          );
        },
      },
    ];

    // Conditionally add Creator column when searching
    if (filterText) {
      baseColumns.push({
        Header: <FormattedMessage id="title.deals.template-creator" defaultMessage="Creator" />,
        accessor: 'templateCreator',
        sortType: createFolderPrioritySortFn((a: any, b: any) => a.localeCompare(b)),
        Cell: ({ cell: { value } }: ColumnsCellType) => {
          return <span>{value}</span>;
        },
      });
    }

    // Add remaining columns
    baseColumns.push(
      {
        Header: <FormattedMessage id="title.deals.template-type" defaultMessage="Type" />,
        accessor: 'templateType',
        sortType: createFolderPrioritySortFn((a: any, b: any) => a.localeCompare(b)),
        Cell: ({
          cell: {
            value,
            row: {
              values: { template },
              original,
            },
          },
        }: ColumnsCellType & { cell: { row: { original: any } } }) => {
          // Show template count for virtual folders
          if (template?.isVirtualSharedFolder && original?.templateCount) {
            return (
              <span className="text-muted">
                {original.templateCount} {original.templateCount === 1 ? 'template' : 'templates'}
              </span>
            );
          }
          return <span>{value}</span>;
        },
      },
      {
        Header: <FormattedMessage id="title.deals.template-status" defaultMessage="Status" />,
        accessor: 'status',
        sortType: createFolderPrioritySortFn(statusSortFn),
        Cell: ({
          cell: {
            row: {
              values: { status, template },
              original,
            },
          },
        }: ColumnsCellType & { cell: { row: { original: any } } }) => {
          const selectedTemplate = documentTemplatesData?.getDocumentTemplates?.find((t: any) => t.id === template.id);
          const publishStatus: TemplatePublishStatus | undefined = original?.publishStatus;

          return (
            <span className={styles.templateStatus}>
              {publishStatus !== 'draft' && (
                <FormattedMessage id="phrase.template-status-readout" defaultMessage={status} values={{ status }} />
              )}
              {publishStatus && publishStatus !== 'published' && (
                <TemplatePublishStatusBadge status={publishStatus} className={publishStatus === 'draft' ? 'ml-0' : undefined} />
              )}
              {selectedTemplate?.sharedToAffiliatedWorkspaces && !template.sharedFromTeam && (
                <Tooltip
                  placement="bottom"
                  testId="workspace-share-tooltip"
                  icon={<GlobeIcon className="text-primary ml-2" style={{ width: '16px', height: '16px' }} />}
                >
                  Shared to other workspaces
                </Tooltip>
              )}
            </span>
          );
        },
      },
      {
        Header: <FormattedMessage id="title.templates.last-updated" defaultMessage="Last Updated" />,
        accessor: 'updatedAt',
        sortType: createFolderPrioritySortFn(dateSortFn),
        Cell: ({ cell: { value } }: any) => {
          return <span className="text-nowrap">{value && dayjs(value).format('LL')}</span>;
        },
      },
      {
        Header: (
          <span className="sr-only">
            <FormattedMessage id="title.deals.detailsLink" defaultMessage="Link to Deal Details" />
          </span>
        ),
        accessor: 'template',
        sortType: () => 0, // No sorting for action column
        Cell: ({
          cell: {
            row: {
              values: { status, template },
              original,
            },
          },
        }: ColumnsCellType & { cell: { row: { original: any } } }) => {
          const selectedTemplateData = documentTemplatesData?.getDocumentTemplates?.find(
            (fetchedTemplate: any) => fetchedTemplate.id === template.id
          );

          return (
            <div
              onClick={(evt: MouseEvent) => {
                evt.stopPropagation();
              }}
              className={styles.actionsTableColumn}
            >
              {template.isFolder ? (
                // Folder actions - different from template actions
                <FolderControls
                  folderId={template.id}
                  folderName={original.name || 'Unknown Folder'}
                  editPermission={canEditInSelectedContext && (isAdmin || canEdit || canManageTemplates)}
                  managePermission={canEditInSelectedContext && (isAdmin || canManageTemplates)}
                  teamId={teamId}
                  onSuccess={() => {
                    templateIsUpdated();
                    refetchFolders();
                  }}
                />
              ) : selectedTemplateData?.sharedFromTeam ? (
                // Workspace-shared template - read only
                <div className={styles.templatesTooltipContainer}>
                  <Tooltip placement="bottom" testId="workspace-shared-template-tooltip">
                    This template is shared from {selectedTemplateData.sharedFromTeam.name} workspace and cannot be
                    edited here.
                  </Tooltip>
                </div>
              ) : !selectedCreatorIsOwnTeam && !template.fromOrganization ? (
                // Context-based restriction - viewing other creator's templates
                <div className={styles.templatesTooltipContainer}>
                  <Tooltip placement="bottom" testId="context-restricted-template-tooltip">
                    This template belongs to {selectedCreator} and cannot be edited here.
                  </Tooltip>
                </div>
              ) : !template.fromOrganization ? (
                <Controls
                  status={status || ''}
                  editPermission={canEditInSelectedContext && (isAdmin || canEdit || canManageTemplates)}
                  managePermission={canEditInSelectedContext && (isAdmin || canManageTemplates)}
                  selectedTemplateData={selectedTemplateData}
                  teamId={teamId}
                  onSuccess={templateIsUpdated}
                  templateType={TemplateType.DOCUMENT}
                />
              ) : (
                <div className={styles.templatesTooltipContainer}>
                  <Tooltip placement="bottom" testId="organization-template-tooltip">
                    This Template is available due to your active membership in {template.organizationName}, it cannot
                    be edited.
                  </Tooltip>
                </div>
              )}
            </div>
          );
        },
      }
    );

    return baseColumns;
  }, [
    templateIsUpdated,
    canEdit,
    canEditInSelectedContext,
    canManageTemplates,
    isAdmin,
    documentTemplatesData,
    teamId,
    permissions,
    selectedCreator,
    selectedCreatorIsOwnTeam,
    filterText,
    refetchFolders,
  ]);

  // Template actions similar to clause actions - always show button but change behavior for free users
  const templateActions = {
    buttonText: 'Add New Template',
    onClick: () =>
      canCreateTemplatesGlobally
        ? null // Will use dropdown
        : setSubscriptionModalActive(true),
    isAvailable: canCreateTemplates,
  };

  const createTemplateActions: CreateTemplateAction[] = [
    {
      text: AddTemplateActionStatus.IMPORT_FROM_WORD,
      allowedType: CreatedTemplateTypes.IMPORTED_WORD,
      open: handleDocFileUploadModal,
      isAvailable: true,
    },
    {
      text: AddTemplateActionStatus.UPLOADED_PDF,
      allowedType: CreatedTemplateTypes.IMPORTED,
      open: handleFileUploadModal,
      isAvailable: canPublishTemplates,
    },
    {
      text: AddTemplateActionStatus.BLANK_TEMPLATE,
      allowedType: CreatedTemplateTypes.BLANK,
      open: () => onAddTemplateShow(TemplateType.DOCUMENT),
      isAvailable: true,
    },
  ];

  const renderCreateTemplateItems = () =>
    createTemplateActions
      .filter((action) => action.isAvailable)
      .map((action) => (
        <Dropdown.Item
          key={action.text}
          as={Button}
          variant="link"
          onClick={() => {
            setShowActionsDropdown(false);
            if (creatableTemplateTypes && creatableTemplateTypes.includes(action.allowedType)) {
              action.open();
            } else {
              setSubscriptionModalActive(true);
            }
          }}
        >
          {action.text}
        </Dropdown.Item>
      ));

  const onTemplateDetail = (template: any) => {
    // If it's a folder, navigate into the folder instead of opening a template
    if (template.isFolder) {
      const newPath = [...folderPath, { id: template.id, name: template.name || 'Folder' }];
      setFolderPath(newPath);
      setCurrentFolderId(template.id);
      return;
    }

    // Otherwise, navigate to the template document
    const templateUrl = fillPlaceholders(
      !template.fromOrganization ? paths.templates.document : paths.templates.orgTemplate,
      {
        templateId: template.id,
        teamId,
      }
    );

    navigate(templateUrl);
  };

  const teamTemplatesData = useMemo(() => {
    let templatesForDisplay = allTemplates;

    // First pass: determine which templates should be shown (for folder inclusion logic)
    const templatesPassingCreatorFilter = templatesForDisplay.filter((template: any) => {
      // Handle creator filtering based on selected creator
      if (selectedCreator) {
        if (selectedCreatorIsOwnTeam) {
          // When viewing "Your Team", show our own templates (excluding shared ones)
          const isOwnTemplate = template.team?.id === teamId && !template.sharedFromTeam;
          return isOwnTemplate;
        } else {
          // When viewing other creators, show templates from that creator
          const isSharedFromSelectedCreator = template.sharedFromTeam?.name === selectedCreator;
          const isFromSelectedOrganization = template.fromOrganization && template.team?.name === selectedCreator;
          const isFromSelectedTeamDirect = template.team?.name === selectedCreator && !template.sharedFromTeam;

          return isSharedFromSelectedCreator || isFromSelectedOrganization || isFromSelectedTeamDirect;
        }
      }
      return true;
    });

    // Extract folder IDs from templates that will be displayed
    const relevantFolderIds = new Set<string>();
    templatesPassingCreatorFilter.forEach((template) => {
      if (template.folder?.id) {
        relevantFolderIds.add(template.folder.id);
      }
    });

    // Second pass: full template filtering including folder logic
    const filteredTemplates = templatesForDisplay.filter((template: any) => {
      // Filter by current folder only when not searching AND viewing own team (for folder navigation)
      if (!filterText && selectedCreatorIsOwnTeam) {
        const templateFolderId = template.folder?.id || null;
        if (templateFolderId !== currentFolderId) {
          return false;
        }
      }

      // When viewing other creators, handle virtual folder navigation
      if (!filterText && selectedCreator && !selectedCreatorIsOwnTeam) {
        if (currentVirtualFolderId) {
          // If viewing inside a virtual folder, only show templates from that folder
          const templateVirtualFolderId = template.folder?.id ? `virtual-${template.folder.id}` : null;
          if (templateVirtualFolderId !== currentVirtualFolderId) {
            return false;
          }
        } else {
          // If at root level, hide templates that belong to folders (they'll be shown via folder navigation)
          if (template.folder?.id) {
            return false;
          }
        }
      }

      const templateType = getTemplateType(template);

      const templateDisplayStatus = templateFilterStatus(template);
      const hasTemplateActualStatus = filterStatuses.has(templateDisplayStatus);
      const hasTemplateActualTemplateType = filterTemplateTypes.has(templateType);

      const hasTemplateFilteredStatus = filterStatuses.size && filterStatuses.size < statusOptions.length;
      const hasTemplateFilteredType = filterTemplateTypes.size && filterTemplateTypes.size < templateTypeOptions.length;

      // Handle creator filtering based on selected creator
      if (selectedCreator) {
        if (selectedCreatorIsOwnTeam) {
          // When viewing "Your Team", show our own templates (excluding shared ones)
          const isOwnTemplate = template.team?.id === teamId && !template.sharedFromTeam;
          if (!isOwnTemplate) {
            return false;
          }
        } else {
          // When viewing other creators, show templates from that creator
          const isSharedFromSelectedCreator = template.sharedFromTeam?.name === selectedCreator;
          const isFromSelectedOrganization = template.fromOrganization && template.team?.name === selectedCreator;
          const isFromSelectedTeamDirect = template.team?.name === selectedCreator && !template.sharedFromTeam;

          if (!isSharedFromSelectedCreator && !isFromSelectedOrganization && !isFromSelectedTeamDirect) {
            return false;
          }
        }
      }

      const textFilter = filterText && !fuzzyInputSearch({ filteredText: filterText, searchValue: template.name });
      const statusFilter = hasTemplateFilteredStatus && !hasTemplateActualStatus;
      const typeFilter = hasTemplateFilteredType && !hasTemplateActualTemplateType;

      return !(textFilter || statusFilter || typeFilter);
    });

    const processedTemplates =
      filteredTemplates.map((template: any) => {
        const templateType = getTemplateType(template);

        return {
          type: 'template',
          template: {
            id: template.id,
            fromOrganization: template.fromOrganization,
            organizationName: template.team?.organization?.name,
            description: template.description || '',
            sharedFromTeam: template.sharedFromTeam,
          },
          name: formatMessage({
            id: 'format.name',
            defaultMessage: template.name,
          }),
          status: template.hidden ? 'inactive' : 'active',
          publishStatus: ownTemplatePublishStatus(template),
          updatedAt: template.updatedAt,
          templateType: templateType,
          templateCreator: template.sharedFromTeam ? template.sharedFromTeam.name : template.team?.name || '',
          folderPath:
            template.folder?.path || template.folder?.name
              ? `Templates > ${Array.isArray(template.folder.path) ? template.folder.path.join(' > ') : (template.folder.path || template.folder.name)}`
              : null,
          sharedFromTeam: template.sharedFromTeam,

          onRowClick: () => {
            const templateUrl = fillPlaceholders(
              !template.fromOrganization ? paths.templates.document : paths.templates.orgTemplate,
              {
                templateId: template.id,
                teamId,
              }
            );

            navigate(templateUrl);
          },
        };
      }) ?? [];

    // Process folders
    let filteredFolders: any[] = [];

    if (selectedCreator && !selectedCreatorIsOwnTeam) {
      // When viewing other creators, create virtual folders from template folder paths
      // Don't show virtual folders when searching - only show templates
      if (filterText) {
        filteredFolders = [];
      } else if (!currentVirtualFolderId) {
        // Only show virtual folders at root level (not when inside a virtual folder)
        const folderMap = new Map<string, { name: string; templates: any[] }>();

        templatesPassingCreatorFilter.forEach((template) => {
          if (template.folder) {
            const folderId = `virtual-${template.folder.id}`;
            const folderName = template.folder.name || template.folder.path;

            if (!folderMap.has(folderId)) {
              folderMap.set(folderId, { name: folderName, templates: [] });
            }
            folderMap.get(folderId)!.templates.push(template);
          }
        });

        // Create virtual folder items
        filteredFolders = Array.from(folderMap.entries()).map(([folderId, folderData]) => ({
          type: 'folder',
          template: {
            id: folderId,
            isFolder: true,
            isVirtualFolder: true,
          },
          name: folderData.name,
          status: 'active',
          updatedAt: new Date().toISOString(),
          templateType: 'Folder',
          templateCreator: selectedCreator,
          team: { name: selectedCreator },
          folderPath: `Templates > ${folderData.name}`,
          onRowClick: () => {
            setCurrentVirtualFolderId(folderId);
            const newPath = [...folderPath, { id: folderId, name: folderData.name, isVirtual: true }];
            setFolderPath(newPath);
          },
        }));
      } else {
        // When inside a virtual folder, don't show any folders
        filteredFolders = [];
      }
    } else {
      // When viewing own team, use regular folder filtering
      // Don't show ANY folders when searching by template name - only show templates
      if (filterText) {
        filteredFolders = [];
      } else {
        filteredFolders =
          templateFoldersData?.getTemplateFolders
            ?.filter((folder: any) => {
              // Filter by current folder only when not searching
              const folderParentId = folder.parentFolder?.id || null;
              if (folderParentId !== currentFolderId) {
                return false;
              }
              return true;
            })
            .map((folder: any) => ({
              type: 'folder',
              template: {
                id: folder.id,
                isFolder: true,
              },
              name: folder.name,
              status: 'active',
              updatedAt: folder.updatedAt,
              templateType: 'Folder',
              templateCreator:
                allTemplates.find((template: any) => template?.team?.id === teamId)?.team?.name || 'Your Team',
              team: {
                name: allTemplates.find((template: any) => template?.team?.id === teamId)?.team?.name || 'Your Team',
              },
              folderPath: folder.path ? `Templates > ${Array.isArray(folder.path) ? folder.path.join(' > ') : folder.path}` : null,
              onRowClick: () => {
                // Navigate to folder
                const newPath = [...folderPath, { id: folder.id, name: folder.name }];
                setFolderPath(newPath);
                setCurrentFolderId(folder.id);
              },
            })) ?? [];
      }
    }

    // Combine folders and templates (sorting will be handled by react-table with folder priority)
    const allItems = [...filteredFolders, ...processedTemplates];
    return allItems;
  }, [
    allTemplates,
    templateFoldersData,
    formatMessage,
    filterText,
    filterStatuses,
    selectedCreator,
    selectedCreatorIsOwnTeam,
    filterTemplateTypes,
    teamId,
    sorting,
    navigate,
    currentFolderId,
    currentVirtualFolderId,
    folderPath,
  ]);

  // All folders for creation modal folder selector
  const allFoldersForCreation = templateFoldersData?.getTemplateFolders ?? [];

  const sortBy = useMemo(() => {
    switch (sorting) {
      case 'ascName':
        return {
          id: 'name',
          desc: false,
        };
      case 'descName':
        return {
          id: 'name',
          desc: true,
        };
      case 'statusActive':
        return {
          id: 'status',
          desc: false,
        };
      case 'statusArchived':
        return {
          id: 'status',
          desc: true,
        };
      case 'dateToLatest':
        return {
          id: 'updatedAt',
          desc: false,
        };
      case 'dateToNewest':
        return {
          id: 'updatedAt',
          desc: true,
        };

      default:
        return {
          id: 'updatedAt',
          desc: false,
        };
    }
  }, [sorting]);

  const table = useTable(
    {
      data: teamTemplatesData,
      //@ts-ignore
      columns,
      initialState: {
        sortBy: [sortBy],
        pageSize: 50,
        pageIndex: 0,
      },
      autoResetPage: false,
    } as any,
    useSortBy,
    usePagination
  );

  // Reset to first page when filters change (autoResetPage is false project-wide)
  useEffect(() => {
    table.gotoPage(0);
  }, [filterStatuses, filterTemplateTypes, filterText, selectedCreator]);

  // Redirect free users to deals page with upgrade modal
  useEffect(() => {
    if (!restrictionsLoading && !hasAccessToTemplates) {
      navigate(fillPlaceholders(paths.deals.landing, { teamId }) + '?upgrade=templates');
    }
  }, [restrictionsLoading, hasAccessToTemplates, navigate, teamId]);

  // Early return to prevent rendering for users without access
  if (!restrictionsLoading && !hasAccessToTemplates) {
    return null;
  }

  if (isDocumentTemplatesDataLoading || isUserOrgsTemplatesDataLoading) {
    return <LoadingSpinner />;
  }

  return (
    <DropdownProvider>
      <Helmet title={formatMessage({ id: 'title.templates', defaultMessage: 'Templates' })} />
      {isSubscriptionModalActive && (
        <SubscriptionRequiredModal
          isOpen={isSubscriptionModalActive}
          onClose={() => {
            setSubscriptionModalActive(false);
          }}
        />
      )}
      <ContentHeader>
        <Row>
          <Col lg={12}>
            <div className="ml-3 mr-3">
              <div className="d-flex align-items-center" style={{ gap: '16px' }}>
                <div data-testid="deals-header" role={'heading'}>
                  <FormattedMessage id="title.templates" defaultMessage="Templates" />
                </div>
                {/* Analytics button - disabled if user's team has no templates, hidden on mobile */}
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="d-none d-md-flex"
                  onClick={() => setShowAnalytics(true)}
                  disabled={!allTemplates.some(t => t.team?.id === teamId && !t.sharedFromTeam)}
                  style={{
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 12px'
                  }}
                  title={!allTemplates.some(t => t.team?.id === teamId && !t.sharedFromTeam)
                    ? 'Your team needs to create templates to view analytics'
                    : 'View template usage analytics'}
                >
                  <ChartLineIcon style={{ width: '16px', height: '16px' }} />
                  <span style={{ lineHeight: 1 }}>
                    <FormattedMessage id="cta.analytics" defaultMessage="Analytics" />
                  </span>
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </ContentHeader>
      <ContentBody className={styles.contentBodyFull}>
        {/* Desktop Search/Filters */}
        <div className="mb-3 non-mobile mx-3">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center" style={{ gap: '12px' }}>
              {/* 1. Search Input - unchanged */}
              <SearchInput
                className={styles.searchInboxBox}
                value={filterText}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFilterText(event.currentTarget.value)}
                placeholder={formatMessage({
                  id: 'deals.filter.title.placeholder',
                  defaultMessage: 'Search template name',
                })}
                aria-label={formatMessage({
                  id: 'deals.filter.title.label',
                  defaultMessage: 'Name',
                })}
              />

              {/* 2. Org Selector - always visible */}
              <div className={styles.creatorDropdownContainer}>
                <Dropdown show={showCreatorDropdown} onToggle={setShowCreatorDropdown}>
                  <Dropdown.Toggle
                    id="dropdown-filter-creator"
                    data-testid="dropdown-filter-creator"
                    as={DropdownToggle}
                    className={styles.creatorDropdown}
                  >
                    <span className={styles.creatorText} title={selectedCreator}>
                      <strong>From:</strong> {selectedCreator || 'Select Org'}
                    </span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <DropdownBody>
                      {allCreators.map((creator: string) => (
                        <Dropdown.Item
                          key={creator}
                          as={Button}
                          variant="link"
                          onClick={() => {
                            setSelectedCreator(creator);
                            setShowCreatorDropdown(false);
                          }}
                          className={selectedCreator === creator ? 'font-weight-bold' : ''}
                        >
                          {creator}
                        </Dropdown.Item>
                      ))}
                    </DropdownBody>
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              {/* 3. Combined Filter Dropdown */}
              <div className={styles.combinedFilterContainer}>
                <Dropdown>
                  <Dropdown.Toggle id="dropdown-combined-filters" as={DropdownToggle}>
                    {getCombinedFilterLabel}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className={styles.filterDropdownMenu}>
                    <DropdownBody>
                      {/* Status Section */}
                      <Dropdown.Header>Status</Dropdown.Header>
                      <DropdownItem highlightable>
                        <Checkbox
                          checked={filterStatuses.size === statusOptions.length}
                          indeterminate={filterStatuses.size > 0 && filterStatuses.size < statusOptions.length}
                          id="dropdown-filter-statuses-_all"
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const checkbox = event.currentTarget;
                            if (checkbox.checked) {
                              setFilterStatuses(new Set(Object.keys(templateStatusMessages)));
                            } else {
                              setFilterStatuses(new Set());
                            }
                          }}
                          label={formatMessage({
                            id: 'deals.filter.status.all-statuses',
                            defaultMessage: 'Any status',
                          })}
                        />
                      </DropdownItem>
                      {statusOptions.map(({ value: status, label }) => (
                        <DropdownItem key={status} highlightable>
                          <Checkbox
                            key={status}
                            value={status}
                            checked={filterStatuses.has(status)}
                            id={`dropdown-filter-statuses-${slugify(status)}`}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              const checkbox = event.currentTarget;
                              const newSet = new Set(filterStatuses);
                              if (checkbox.checked) newSet.add(checkbox.value);
                              else newSet.delete(checkbox.value);
                              setFilterStatuses(newSet);
                            }}
                            label={<FormattedMessage {...label} />}
                          />
                        </DropdownItem>
                      ))}
                      
                      {/* Type Section */}
                      <Dropdown.Header>Type</Dropdown.Header>
                      <DropdownItem highlightable>
                        <Checkbox
                          checked={filterTemplateTypes.size === templateTypeOptions.length}
                          indeterminate={
                            filterTemplateTypes.size > 0 && filterTemplateTypes.size < templateTypeOptions.length
                          }
                          id="dropdown-filter-templateTypes-_all"
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const checkbox = event.currentTarget;
                            if (checkbox.checked) {
                              setFilterTemplateTypes(new Set(Object.keys(templateTypeMessages)));
                            } else {
                              setFilterTemplateTypes(new Set());
                            }
                          }}
                          label={formatMessage({
                            id: 'deals.filter.templateTypes.all-templateTypes',
                            defaultMessage: 'All Types',
                          })}
                        />
                      </DropdownItem>
                      {templateTypeOptions.map(({ value: templateType, label }) => (
                        <DropdownItem key={templateType} highlightable>
                          <Checkbox
                            key={templateType}
                            value={templateType}
                            checked={filterTemplateTypes.has(templateType)}
                            id={`dropdown-filter-templateTypes-${slugify(templateType)}`}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              const checkbox = event.currentTarget;
                              const newSet = new Set(filterTemplateTypes);
                              if (checkbox.checked) newSet.add(checkbox.value);
                              else newSet.delete(checkbox.value);
                              setFilterTemplateTypes(newSet);
                            }}
                            label={<FormattedMessage {...label} />}
                          />
                        </DropdownItem>
                      ))}
                    </DropdownBody>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>

            {/* 4. Action Buttons */}
            <div className={styles.actionsDropdownContainer}>
              {templateActions.isAvailable && (
                <>
                  {canCreateTemplatesGlobally ? (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {canPublishTemplates && (
                        <Button variant="primary" onClick={() => setShowCreateFolderModal(true)}>
                          <FormattedMessage id="cta.new-folder" defaultMessage="New Folder" />
                        </Button>
                      )}
                      <Dropdown show={showActionsDropdown} onToggle={setShowActionsDropdown}>
                        <Dropdown.Toggle id="dropdown-template-actions" variant={'secondary'} as={DropdownToggleOrange}>
                          <span className={styles.dropdownTitle}>
                            <FormattedMessage id="template.new-template" defaultMessage="New Template" />
                          </span>
                        </Dropdown.Toggle>
                        <Dropdown.Menu className={styles.actionsDropdownMenu}>
                          <div className={styles.sendDropdownBodyWrapper}>
                            <DropdownBody>{renderCreateTemplateItems()}</DropdownBody>
                          </div>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  ) : (
                    <Button data-testid="createTemplate" variant="secondary" onClick={templateActions.onClick}>
                      <TitleFormattedMessage id="cta.create-template" defaultMessage={templateActions.buttonText} />
                    </Button>
                  )}
                  <PropdocsModal
                    show={showAddTemplate}
                    setShow={onAddTemplateModalHide}
                    size="md"
                    title={
                      <FormattedMessage
                        id="title.add-team-member"
                        defaultMessage={`Create ${AddTemplateActionStatus.BLANK_TEMPLATE}`}
                      />
                    }
                  >
                    <AddNewTemplateFormik
                      bodyContainer={React.Fragment}
                      actionsContainer={React.Fragment}
                      renderSecondaryAction={() => (
                        <Button variant="link" className="text-secondary" onClick={onAddTemplateModalHide}>
                          <TitleFormattedMessage id="cta.cancel" defaultMessage="Cancel" />
                        </Button>
                      )}
                      onSuccess={templateIsUpdated}
                      templateFormEditMode={false}
                      templateType={selectedTemplateType}
                      folders={allFoldersForCreation}
                      selectedFolderId={selectedFolderForCreation}
                      onFolderSelectionChange={setSelectedFolderForCreation}
                      foldersLoading={isTemplateFoldersLoading}
                    />
                  </PropdocsModal>
                  <PropdocsModal
                    show={showEditTemplate}
                    setShow={() => setShowEditTemplate(false)}
                    size="sm"
                    title={
                      <FormattedMessage id="title.add-team-member" defaultMessage="Edit Template" />
                    }
                  >
                    <div>{/* Edit template form would go here */}</div>
                  </PropdocsModal>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Search/Filters */}
        <Row className="mobile">
          <Col className="d-flex align-items-center" style={{ padding: '16px', gap: '16px' }}>
            <div className={'d-flex flex-fill'}>
              <SearchInput
                iconClassName="mobileSearch__icon"
                inputClassName="mobileSearch"
                className="mobileSearch__container"
                value={filterText}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFilterText(event.currentTarget.value)}
                placeholder={formatMessage({
                  id: 'deals.filter.title.placeholder',
                  defaultMessage: 'Search template name',
                })}
                aria-label={formatMessage({
                  id: 'deals.filter.title.label',
                  defaultMessage: 'Name',
                })}
              />

              <Button className="filterBtn" onClick={() => setShowFilter(true)} data-testid="mobileFilterBtn">
                <FilterIcon width={24} height={24} />
              </Button>

              {templateActions.isAvailable &&
                (canCreateTemplatesGlobally ? (
                  <Dropdown show={showActionsDropdown} onToggle={setShowActionsDropdown}>
                    <Dropdown.Toggle
                      id="dropdown-mobile-template-actions"
                      as={Button}
                      className="addBtn"
                      style={{ marginLeft: '0.5rem' }}
                    >
                      <PlusIcon width={24} height={24} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu className={styles.actionsDropdownMenu}>
                      <div className={styles.sendDropdownBodyWrapper}>
                        <DropdownBody>
                          {renderCreateTemplateItems()}
                          {canPublishTemplates && (
                            <>
                              <div className={styles.dropdownDivider} />
                              <Dropdown.Item
                                as={Button}
                                variant="link"
                                onClick={() => {
                                  setShowActionsDropdown(false);
                                  setShowCreateFolderModal(true);
                                }}
                              >
                                New Folder
                              </Dropdown.Item>
                            </>
                          )}
                        </DropdownBody>
                      </div>
                    </Dropdown.Menu>
                  </Dropdown>
                ) : (
                  <Button
                    className="addBtn"
                    onClick={templateActions.onClick}
                    style={{ marginLeft: '0.5rem' }}
                  >
                    <PlusIcon width={24} height={24} />
                  </Button>
                ))}
            </div>
          </Col>
        </Row>

        {/* Breadcrumb Navigation */}
        <div className="mb-2 non-mobile mx-3">
          <div className="d-flex align-items-center" style={{ fontWeight: 'bold', fontSize: '14px' }}>
              <HomeIcon width={18} height={18} className="text-primary mr-2" style={{ marginTop: '-1px' }} />
              {folderPath.map((pathItem, index) => (
                <React.Fragment key={pathItem.id || 'root'}>
                  {index === folderPath.length - 1 ? (
                    <span className="text-dark">{pathItem.name}</span>
                  ) : (
                    <>
                      <Button
                        variant="link"
                        className="p-0 text-decoration-none text-primary"
                        style={{ fontWeight: 'bold', fontSize: '14px' }}
                        onClick={() => {
                          const newPath = folderPath.slice(0, index + 1);
                          setFolderPath(newPath);
                          if (pathItem.isVirtual) {
                            setCurrentVirtualFolderId(pathItem.id);
                            setCurrentFolderId(null);
                          } else {
                            setCurrentFolderId(pathItem.id);
                            setCurrentVirtualFolderId(null);
                          }
                        }}
                      >
                        {pathItem.name}
                      </Button>
                      <span className="mx-2 text-muted">{'>'}</span>
                    </>
                  )}
                </React.Fragment>
              ))}
          </div>
        </div>

        {/* Desktop Table */}
        <div className={`${styles.scrollableContainer} non-mobile`}>
          <Row>
            <Col>
              {teamTemplatesData.length ? (
                <div>
                  <Table striped isStickyHeader isActionColumn paginationBelow isStickyPagination table={table} />
                </div>
              ) : (
                <Container className="mt-5">
                  <div className="lead text-center">
                    <h2>
                      <FormattedMessage id="title.templates-history" defaultMessage="No Templates" />
                    </h2>
                    <FormattedMessage
                      id="phrase.templates.no-templates"
                      defaultMessage="Your team's templates will appear here."
                    />
                  </div>
                </Container>
              )}
            </Col>
          </Row>
        </div>

        {/* Mobile Table */}
        <div className={styles.mobileTableContainer}>
          {teamTemplatesData.length ? (
            <>
              <div className={styles.mobileListArea}>
                <MobileTable>
                {table.page?.map((row: any) => {
                  const template = row.original;
                  return (
                    <MobileTemplateItem
                      key={`mobile-template-${template.template.id}`}
                      template={template}
                      isAdmin={isAdmin}
                      canEdit={canEditInSelectedContext && canEdit}
                      canManageTemplates={canEditInSelectedContext && canManageTemplates}
                      teamId={teamId}
                      selectedTemplate={documentTemplatesData?.getDocumentTemplates?.find(
                        (tmp: any) => tmp.id === template.template.id
                      )}
                      templateIsUpdated={templateIsUpdated}
                      onClick={() => onTemplateDetail(template.template)}
                      context="templates"
                      refetchFolders={refetchFolders}
                    />
                  );
                })}
                </MobileTable>
              </div>
              <MobilePagination
                pageIndex={table.state.pageIndex}
                pageCount={table.pageCount}
                canPreviousPage={table.canPreviousPage}
                canNextPage={table.canNextPage}
                gotoPage={table.gotoPage}
                previousPage={table.previousPage}
                nextPage={table.nextPage}
                totalItems={teamTemplatesData.length}
                pageSize={table.state.pageSize}
              />
            </>
          ) : (
            <Container className="mt-5">
              <div className="lead text-center">
                <h2>
                  <FormattedMessage id="title.templates-history" defaultMessage="No Templates" />
                </h2>
                <FormattedMessage
                  id="phrase.templates.no-templates"
                  defaultMessage="Your team's templates will appear here."
                />
              </div>
            </Container>
          )}
        </div>
      </ContentBody>
      <FileUploadModal
        onClose={() => setActiveModal(null)}
        isOpen={activeModal === ModalActionsEnum.UPLOAD_FILE || activeModal === ModalActionsEnum.UPLOAD_DOC_FILE}
        teamId={teamId}
        templateUpload
        folders={allFoldersForCreation}
        selectedFolderId={selectedFolderForCreation}
        onFolderSelectionChange={setSelectedFolderForCreation}
        foldersLoading={isTemplateFoldersLoading}
      />
      <FilterSidebar
        isOpen={showFilter}
        filterTypes={filterTemplateTypes}
        filterStatuses={filterStatuses}
        selectedCreator={selectedCreator}
        onClose={() => setShowFilter(!showFilter)}
        allCreators={allCreators}
        setSorting={setSorting}
        setFilterTypes={setFilterTemplateTypes}
        setFilterStatuses={setFilterStatuses}
        setSelectedCreator={setSelectedCreator}
      />
      <CreateFolderModal
        isOpen={showCreateFolderModal}
        onClose={() => setShowCreateFolderModal(false)}
        teamId={teamId}
        onFolderCreated={() => {
          setShowCreateFolderModal(false);
          refetch();
          refetchFolders();
        }}
      />
      {/* Template Analytics Slideout */}
      <TemplateAnalyticsSlideout
        isOpen={showAnalytics}
        onClose={() => setShowAnalytics(false)}
        teamId={teamId}
      />
    </DropdownProvider>
  );
};

export default TemplatesLandingPage;
